import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const isHindi = language === 'hi';

  const navigationItems = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.blog'), path: '/blog' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {t('home.title')}
        </Typography>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                backgroundColor: location.pathname === item.path ? 'primary.light' : 'transparent',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            >
              <ListItemText 
                primary={item.label} 
                sx={{ 
                  color: location.pathname === item.path ? 'white' : 'text.primary',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ px: 2, mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
          Language:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: '4px 8px' }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setLanguage('en')}
            sx={{
              minWidth: 'auto',
              padding: '2px 6px',
              fontSize: '0.875rem',
              fontWeight: !isHindi ? 'bold' : 'normal',
              color: !isHindi ? 'primary.main' : 'text.secondary',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            EN
          </Button>
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>|</Box>
          <Button
            variant="text"
            size="small"
            onClick={() => setLanguage('hi')}
            sx={{
              minWidth: 'auto',
              padding: '2px 6px',
              fontSize: '0.875rem',
              fontWeight: isHindi ? 'bold' : 'normal',
              color: isHindi ? 'primary.main' : 'text.secondary',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            HI
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          startIcon={<PhoneIcon />}
          href="tel:+919838167840"
          variant="outlined"
          size="small"
          fullWidth
        >
          {isHindi ? 'कॉल करें' : 'Call Now'}
        </Button>
        <Button
          startIcon={<WhatsAppIcon />}
          href="https://wa.me/919838167840"
          target="_blank"
          variant="contained"
          size="small"
          fullWidth
          sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#128C7E' } }}
        >
          WhatsApp
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={1}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo/Title */}
          <Typography
            variant="h5"
            component={Link}
            to="/"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
            }}
          >
            {t('home.title')}
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    borderBottom: location.pathname === item.path ? '2px solid' : 'none',
                    borderBottomColor: 'primary.main',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right side buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Language Toggle */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: '4px 8px' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setLanguage('en')}
                  sx={{
                    minWidth: 'auto',
                    padding: '2px 6px',
                    fontSize: '0.875rem',
                    fontWeight: !isHindi ? 'bold' : 'normal',
                    color: !isHindi ? 'primary.main' : 'text.secondary',
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  EN
                </Button>
                <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>|</Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setLanguage('hi')}
                  sx={{
                    minWidth: 'auto',
                    padding: '2px 6px',
                    fontSize: '0.875rem',
                    fontWeight: isHindi ? 'bold' : 'normal',
                    color: isHindi ? 'primary.main' : 'text.secondary',
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  HI
                </Button>
              </Box>
            )}

            {/* Contact buttons for desktop */}
            {!isMobile && (
              <>
                <IconButton
                  href="tel:+919838167840"
                  sx={{ color: 'primary.main' }}
                >
                  <PhoneIcon />
                </IconButton>
                <Button
                  startIcon={<WhatsAppIcon />}
                  href="https://wa.me/919838167840"
                  target="_blank"
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: '#25D366',
                    '&:hover': { backgroundColor: '#128C7E' },
                    minWidth: 'auto',
                    px: 1,
                  }}
                >
                  WhatsApp
                </Button>
              </>
            )}

            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>



      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          elevation: 4,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;